import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Card, CircularProgress, Typography, Divider, Link } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import Seo from '../components/Seo';

/**
 * Public cleaning checklist for a job, linked from its invoice
 * (/checklist/:token). Shows the company's task-list template for the
 * job's cleaning type, grouped by area. No authentication required.
 */
const ChecklistPage = () => {
  const { token } = useParams();
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${apiBase}/api/public/documents/${token}/checklist`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Unable to load this checklist.');
        setChecklist(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [apiBase, token]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', px: 2 }}>
        <Card sx={{ p: 4, maxWidth: 460, textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom>This checklist isn’t available</Typography>
          <Typography variant="body2" color="text.secondary">{error}</Typography>
        </Card>
      </Box>
    );
  }

  const company = checklist.company || {};
  const service = checklist.service || {};
  const accent = company.primaryColor || '#1976d2';
  const taskCount = checklist.groups.reduce((n, g) => n + g.tasks.length, 0);

  return (
    <>
      <Seo title="Cleaning Checklist" noindex />
      <Box sx={{ maxWidth: 760, mx: 'auto', px: 2, py: { xs: 3, sm: 5 } }}>
        <Card sx={{ p: { xs: 3, sm: 4 } }}>
          {company.logoUrl && (
            <Box component="img" src={company.logoUrl} alt={company.name} sx={{ display: 'block', maxHeight: 56, mb: 1 }} />
          )}
          <Typography variant="overline" color="text.secondary">{company.name}</Typography>
          <Typography variant="h5" sx={{ fontWeight: 700, color: accent }}>
            {service.name} checklist
          </Typography>
          {service.description && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {service.description}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {taskCount} task{taskCount !== 1 ? 's' : ''} our team works through on every visit
            {service.url && (
              <>
                {' · '}
                <Link href={service.url} sx={{ color: accent }}>About this service</Link>
              </>
            )}
          </Typography>

          {checklist.groups.length === 0 && (
            <Typography variant="body2" sx={{ mt: 3 }}>No tasks are listed for this service.</Typography>
          )}

          {checklist.groups.map((group) => (
            <Box key={group.category} sx={{ mt: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{group.category}</Typography>
              <Divider sx={{ mb: 1 }} />
              {group.tasks.map((task, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, py: 0.5 }}>
                  <CheckCircleOutlineIcon fontSize="small" sx={{ color: accent, mt: '2px' }} />
                  <Box>
                    <Typography variant="body2">{task.title}</Typography>
                    {task.description && (
                      <Typography variant="caption" color="text.secondary">{task.description}</Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Box>
          ))}
        </Card>
      </Box>
    </>
  );
};

export default ChecklistPage;
